import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Permissions, TableInfoDc } from "@evergis/api";
import { AccessControlListDc } from "@evergis/api/dist/__generated__/data-contracts";

import { Chips } from "./Chips";

import { projectInfosWithDeps } from "../../../../resources/projects";
import { updatePermissions } from "../../../../resources/bulkOperations";
import { isConfigure } from "../../../../utils/permissions";
import { useResultNotifications } from "../hooks/useResultNotifications";

interface ProjectWmsProps {
  projectName: string;
  setIsLoading: (state: boolean) => void;
}

export const ProjectWms: FC<ProjectWmsProps> = ({ projectName, setIsLoading }) => {
  const {
    data: projectsInfo,
    loading,
    refetch,
  } = projectInfosWithDeps.use({ input: [projectName], waitInput: true, immediate: false });

  const projectInfo = useMemo(() => projectsInfo?.projects[0], [projectsInfo]);
  const layers = useMemo(() => projectsInfo?.deps.layers, [projectsInfo]);
  const tables: (TableInfoDc & { acl?: AccessControlListDc })[] = useMemo(
    () => projectsInfo?.deps.tables,
    [projectsInfo],
  );

  const [update, { data }] = updatePermissions.use();

  const unavailableResources = useMemo(() => {
    const result = [];

    layers?.forEach(layer => {
      if (!isConfigure(layer.permissions)) {
        result.push(layer);
      }
    });

    tables?.forEach(table => {
      if (!isConfigure(table.permissions)) {
        result.push(table);
      }
    });

    return result;
  }, [layers, tables]);

  const getPermissionsInput = useCallback(
    (role: string, type: "add" | "remove") => ({
      projects: [
        {
          objectName: projectName,
          data:
            type === "add"
              ? [
                  ...projectInfo?.acl.data.filter(item => item.role !== role),
                  {
                    role,
                    permissions: Permissions.Read,
                  },
                ]
              : [...projectInfo?.acl.data.filter(item => item.role !== role)],
        },
      ],
      tables: tables.map(table => ({
        objectName: table.name,
        data:
          type === "add"
            ? [
                ...table.acl.data.filter(item => item.role !== role),
                {
                  role,
                  permissions: Permissions.Read,
                },
              ]
            : [...table.acl.data.filter(item => item.role !== role)],
      })),
      layers: layers.map(layer => ({
        objectName: layer.name,
        data:
          type === "add"
            ? [
                ...layer.configuration.acl.data.filter(item => item.role !== role),
                {
                  role,
                  permissions: Permissions.Read,
                },
              ]
            : [...layer.configuration.acl.data.filter(item => item.role !== role)],
      })),
    }),
    [projectName, projectInfo?.acl.data, tables, layers],
  );

  const onAdd = useCallback(
    (role: string) => {
      update({
        input: getPermissionsInput(role, "add"),
      }).then(refetch);
    },
    [update, getPermissionsInput, refetch],
  );

  const onRemove = useCallback(
    (role: string) => {
      update({
        input: getPermissionsInput(role, "remove"),
      }).then(refetch);
    },
    [update, getPermissionsInput, refetch],
  );

  useEffect(() => {
    refetch();
  }, []);

  useResultNotifications(data && unavailableResources.length > 0, unavailableResources);

  return (
    <Chips
      resourceLoading={loading}
      resourceAcl={projectInfo?.acl.data}
      onAdd={onAdd}
      onRemove={onRemove}
      setIsLoading={setIsLoading}
    />
  );
};
